import { useSettingStore } from '@/stores/setting'
import { byId, round2, sortByMultiple, sum } from '@/utils/utils'

import { PerformanceAddOnRead, PerformanceTableRead, ReservationAddOnWrite } from '@generated/types'

export interface EnrichedTable extends PerformanceTableRead {
  description: string
  guests: number
  price_per_person: number
  type: string
}

export interface EnrichedAddOn extends PerformanceAddOnRead {
  lineAmountCents: number
  lineTaxCents: number
  quantity: number
}

export interface OrderPricing {
  amount: number
  addOnAmount: number
  tax: number
  serviceFee: number
  discounted: number
  total: number
}

export function seatPartyAtTables(
  tableReads: PerformanceTableRead[],
  guests_remaining: number,
): EnrichedTable[] {
  let sortedTables = sortByMultiple([...tableReads], ['-price_per_person', '-maximum_guests'])
  let result: EnrichedTable[] = []
  for (const table of sortedTables) {
    const seated = Math.min(guests_remaining, table.maximum_guests)
    const tableType = useSettingStore().tableType(table.type_id).name || 'Table'
    result.push({
      ...table,
      description: !table.standing_room ? `${tableType} (${table.name})` : `${table.name}`,
      guests: seated,
      price_per_person: +table.price_per_person || 0,
      type: tableType,
    })
    guests_remaining = guests_remaining - seated
  }
  return result
}

export function seatExtraGuests(
  seatedTables: EnrichedTable[],
  extra_guests: number,
): EnrichedTable[] {
  if (extra_guests > 0) {
    // find the table whose price per person is the highest and add the remaining guests to it
    const highestPricePerPerson = Math.max(...seatedTables.map(t => t.price_per_person))
    const highestPricePerPersonTable = seatedTables.find(
      t => t.price_per_person === highestPricePerPerson,
    )
    if (highestPricePerPersonTable) {
      highestPricePerPersonTable.guests += extra_guests
    }
  }
  return seatedTables
}

export function tablesForSeatedParty(
  performance: { tables?: PerformanceTableRead[] } | null,
  tableIds: number[],
  guests_remaining: number,
): EnrichedTable[] {
  if (!performance?.tables) return []
  const tableReads = tableIds.flatMap(id => performance.tables!.find(byId(id)) || []) // O(n^2) because we want to preserve order
  return seatPartyAtTables(tableReads, guests_remaining)
}

export function priceAddOns(
  performanceAddOns: PerformanceAddOnRead[] | null,
  addOns: ReservationAddOnWrite[] | null,
): EnrichedAddOn[] {
  if (!addOns?.length || !performanceAddOns?.length) return []
  return performanceAddOns
    .map(pAddOn => {
      const rAddOn = addOns.find(addOn => addOn.performance_add_on_id === pAddOn.id) || {
        quantity: 0,
      }
      const lineAmountCents = rAddOn.quantity * pAddOn.price * 100
      const lineTaxCents = (lineAmountCents * pAddOn.tax_rate) / 100
      return { ...pAddOn, lineAmountCents, lineTaxCents, quantity: rAddOn.quantity }
    })
    .filter(addOn => addOn.quantity > 0)
}

export function getZeroPricing(): OrderPricing {
  return {
    amount: 0,
    addOnAmount: 0,
    tax: 0,
    serviceFee: 0,
    discounted: 0,
    total: 0,
  }
}

export function getPricing(
  tables: EnrichedTable[],
  addOns: EnrichedAddOn[],
  numberOfGuests: number,
  serviceFeePerGuest: number,
  servicePercentage: number,
  taxRate: number,
  feeTaxRate: number,
  percentOff: number,
): OrderPricing {
  const discount = x => (+x * (100 - percentOff)) / 100
  const fullAmount = sum(tables.map(t => t.price_per_person * t.guests) || 0)

  const addOnAmount = round2(sum(addOns.map(a => a.price * a.quantity)) || 0)
  const addOnTax = sum(addOns.map(a => a.lineTaxCents)) / 100
  const fullServiceFee =
    (fullAmount * servicePercentage) / 100 + serviceFeePerGuest * numberOfGuests
  const amount = round2(discount(fullAmount))
  const serviceFee = round2(discount(fullServiceFee))
  if (amount + addOnAmount == 0 || amount + serviceFee < 0.5) {
    return getZeroPricing()
  }
  const tax = round2((taxRate * amount + feeTaxRate * serviceFee) / 100 + addOnTax)
  return {
    amount,
    addOnAmount,
    tax,
    serviceFee,
    discounted: round2(fullAmount + fullServiceFee - amount - serviceFee) || 0,
    total: round2(amount + addOnAmount + tax + serviceFee),
  }
}
